import { FlightError } from "@b2bportal/air-booking-api";
import { PurchaseErrorEnum } from "@b2bportal/purchase-api";

import { SeatSelectionErrorModal } from "../../types/common";
import {
  FlightErrorAction,
  type FlightErrorConfig,
  type FlightErrorConfigMap,
  type PurchaseErrorConfigMap,
  type SeatSelectionErrorConfigMap,
} from "./types";

/**
 * Translation key prefix for checkout errors
 */
const PREFIX = "checkoutError";

/**
 * Helper to create a button config
 */
const button = (textKey: string, action: FlightErrorAction) => ({
  textKey: `${PREFIX}.${textKey}`,
  action,
});

/**
 * Common button configurations
 */
const BUTTONS = {
  searchAgain: button("searchAgain", FlightErrorAction.NavigateToFlightSearch),
  findNewFlight: button(
    "findNewFlight",
    FlightErrorAction.NavigateToFlightSearch
  ),
  updateTravelers: button(
    "updateTraveler",
    FlightErrorAction.OpenPassengerPicker
  ),
  updateTravelersGoBack: button(
    "updateTraveler",
    FlightErrorAction.GoBackToPassengerInfo
  ),
  openPassportForm: button(
    "updateTraveler",
    FlightErrorAction.OpenPassportForm
  ),
  ok: button("acknowlegement", FlightErrorAction.NavigateToConfirmation),
  okMyTrips: button("acknowlegement", FlightErrorAction.NavigateToMyTrips),
  tryAgain: button("tryAgain", FlightErrorAction.RetryQuote),
  empty: button("", FlightErrorAction.NavigateToConfirmation),
};

/**
 * Default error configuration for fallback
 */
const DEFAULT_CART_QUOTE_CONFIG: FlightErrorConfig = {
  titleKey: `${PREFIX}.genericErrorTitle`,
  subtitleKey: `${PREFIX}.genericErrorSubtitle`,
  primaryButton: BUTTONS.searchAgain,
};

const DEFAULT_CART_FULFILL_CONFIG: FlightErrorConfig = {
  titleKey: `${PREFIX}.genericErrorTitle`,
  subtitleKey: `${PREFIX}.genericErrorSubtitle`,
  primaryButton: BUTTONS.tryAgain,
  secondaryButton: BUTTONS.findNewFlight,
};

/**
 * Flight Error Configuration Map
 *
 * This configuration maps each FlightError code to its error modal configuration
 * for both CART_QUOTE and CART_FULFILL contexts.
 *
 * Based on the error specification document.
 *
 * Note: When a cell in the spec shows "New X" values, those are the updated values.
 * Otherwise, the current values are used.
 */
export const FLIGHT_ERROR_CONFIG: FlightErrorConfigMap = {
  CART_QUOTE: {
    default: DEFAULT_CART_QUOTE_CONFIG,

    // ============================================================
    // ProductError codes
    // ============================================================

    [FlightError.CheckInMinimumAgeNotMet]: {
      titleKey: `${PREFIX}.checkInMinimumAgeNotMetTitle`,
      subtitleKey: `${PREFIX}.checkInMinimumAgeNotMetSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.DuplicateBooking]: {
      // NEW: Updated from generic error to specific duplicate booking message
      titleKey: `${PREFIX}.duplicateBookingTitle`,
      subtitleKey: `${PREFIX}.duplicateBookingSubtitle`,
      primaryButton: BUTTONS.okMyTrips,
      className: "warn",
    },

    [FlightError.DuplicatedNationalId]: {
      titleKey: `${PREFIX}.duplicatedNationalIdTitle`,
      subtitleKey: `${PREFIX}.duplicatedNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.DuplicatedPassport]: {
      titleKey: `${PREFIX}.duplicatedPassportTitle`,
      // NEW: Updated subtitle to be passport-specific
      subtitleKey: `${PREFIX}.duplicatedPassportSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.ExcessiveActivity]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.ExpiredNationalId]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.passengerValidationTitle`,
      subtitleKey: `${PREFIX}.expiredNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.ExpiredPassport]: {
      titleKey: `${PREFIX}.expiredPassportTitle`,
      subtitleKey: `${PREFIX}.expiredPassportSubtitle`,
      primaryButton: BUTTONS.openPassportForm,
      className: "missing",
    },

    [FlightError.ExpiringNationalId]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.passengerValidationTitle`,
      subtitleKey: `${PREFIX}.expiringNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.ExpiringPassport]: {
      titleKey: `${PREFIX}.expiringPassportTitle`,
      subtitleKey: `${PREFIX}.expiringPassportSubtitle`,
      // NEW: Changed action to go back to passenger info
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.ExtraBagsError]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.IllegalLapInfantKTN]: {
      titleKey: `${PREFIX}.illegalLapInfantKTNTitle`,
      subtitleKey: `${PREFIX}.illegalLapInfantKTNSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.IllegalLapInfantRedress]: {
      titleKey: `${PREFIX}.illegalLapInfantRedressTitle`,
      subtitleKey: `${PREFIX}.illegalLapInfantRedressSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.Inactivity]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.flightsInActivityTitle`,
      subtitleKey: `${PREFIX}.flightsInActivitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
      className: "timeout",
    },

    [FlightError.InvalidCustomer]: {
      titleKey: `${PREFIX}.invalidCustomerTitle`,
      subtitleKey: `${PREFIX}.invalidCustomerSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.InvalidPassengerName]: {
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.invalidPassengerName`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.InvalidPassengers]: {
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.invalidPassengerSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.InvalidPassportNumber]: {
      titleKey: `${PREFIX}.invalidPassportNumberTitle`,
      subtitleKey: `${PREFIX}.invalidPassportNumberSubtitle`,
      primaryButton: BUTTONS.openPassportForm,
      className: "missing",
    },

    [FlightError.InvalidPassportOrNationalIdNumber]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.invalidPassengerSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.LapInfantsUnsupported]: {
      titleKey: `${PREFIX}.lapInfantsUnsupportedTitle`,
      subtitleKey: `${PREFIX}.lapInfantsUnsupportedSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.LapInfantTooOld]: {
      titleKey: `${PREFIX}.lapInfantTooOldTitle`,
      subtitleKey: `${PREFIX}.lapInfantTooOldSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.MalformedKnownTravelerNumber]: {
      titleKey: `${PREFIX}.malformedKnownTravelerNumberTitle`,
      subtitleKey: `${PREFIX}.malformedKnownTravelerNumberSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.MalformedRedressNumber]: {
      titleKey: `${PREFIX}.malformedRedressNumberTitle`,
      subtitleKey: `${PREFIX}.malformedRedressNumberSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.MissingAirlineLocator]: {
      titleKey: `${PREFIX}.missingAirlineLocatorTitle`,
      subtitleKey: `${PREFIX}.missingAirlineLocatorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.MissingNationalId]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.passengerValidationTitle`,
      subtitleKey: `${PREFIX}.missingNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.MissingNationality]: {
      titleKey: `${PREFIX}.missingNationalityTitle`,
      subtitleKey: `${PREFIX}.missingNationalitySubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.MissingPassport]: {
      titleKey: `${PREFIX}.missingPassportTitle`,
      subtitleKey: `${PREFIX}.missingPassportSubtitle`,
      primaryButton: BUTTONS.openPassportForm,
      className: "missing",
    },

    [FlightError.MissingPassportNumber]: {
      titleKey: `${PREFIX}.missingPassportNumberTitle`,
      subtitleKey: `${PREFIX}.missingPassportNumberSubtitle`,
      primaryButton: BUTTONS.openPassportForm,
      className: "missing",
    },

    [FlightError.MissingPassportOrNationalId]: {
      titleKey: `${PREFIX}.missingPassportOrNationalIdTitle`,
      subtitleKey: `${PREFIX}.missingPassportOrNationalIdSubtitle`,
      primaryButton: BUTTONS.openPassportForm,
      className: "missing",
    },

    [FlightError.NoAdultPassenger]: {
      titleKey: `${PREFIX}.noAdultPassengerTitle`,
      subtitleKey: `${PREFIX}.noAdultPassengerSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.NoAvailability]: {
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.NoContactInformation]: {
      titleKey: `${PREFIX}.noContactInformationTitle`,
      subtitleKey: `${PREFIX}.noContactInformationSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.NoTicketlessResponse]: {
      titleKey: `${PREFIX}.noTicketlessResponseTitle`,
      subtitleKey: `${PREFIX}.noTicketlessResponseSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.PassengerNameTooShort]: {
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.passengerNameTooShortSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.PassengerNotFound]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.PassthroughPaymentError]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.ProviderError]: {
      titleKey: `${PREFIX}.providerErrorTitle`,
      subtitleKey: `${PREFIX}.providerErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.SeatedInfantsUnsupported]: {
      titleKey: `${PREFIX}.seatedInfantsUnsupportedTitle`,
      subtitleKey: `${PREFIX}.seatedInfantsUnsupportedSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.SessionClosed]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.ShopAsyncError]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.TicketingNotStarted]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.TicketingUnconfirmed]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.TicketingUnknown]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.TooManyLapInfants]: {
      titleKey: `${PREFIX}.tooManyLapInfantsTitle`,
      subtitleKey: `${PREFIX}.tooManyLapInfantsSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.TooManyPassengers]: {
      titleKey: `${PREFIX}.tooManyPassengersTitle`,
      subtitleKey: `${PREFIX}.tooManyPassengersSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.TransientServiceError]: {
      titleKey: `${PREFIX}.transientServiceErrorTitle`,
      subtitleKey: `${PREFIX}.transientServiceErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.UnexpectedPassengers]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.unexpectedPassengersSubtitle`,
      primaryButton: BUTTONS.findNewFlight,
    },

    [FlightError.Unknown]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.UnknownSabreAppError]: {
      titleKey: `${PREFIX}.unknownSabreAppErrorTitle`,
      subtitleKey: `${PREFIX}.unknownSabreAppErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.UnsupportedNameCharacters]: {
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.unsupportedNameCharactersSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.UnsupportedPaymentKind]: DEFAULT_CART_QUOTE_CONFIG,

    [FlightError.UpgradeRequired]: {
      titleKey: `${PREFIX}.upgradeRequiredTitle`,
      subtitleKey: `${PREFIX}.unknownSabreAppErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
  },

  CART_FULFILL: {
    default: DEFAULT_CART_FULFILL_CONFIG,

    // ============================================================
    // ProductError codes
    // ============================================================

    [FlightError.CheckInMinimumAgeNotMet]: {
      titleKey: `${PREFIX}.checkInMinimumAgeNotMetTitle`,
      subtitleKey: `${PREFIX}.checkInMinimumAgeNotMetSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.DuplicateBooking]: {
      titleKey: `${PREFIX}.duplicateBookingTitle`,
      subtitleKey: `${PREFIX}.duplicateBookingSubtitle`,
      primaryButton: BUTTONS.ok,
      className: "warn",
    },

    [FlightError.DuplicatedNationalId]: {
      // NEW: Updated from generic error with retry
      titleKey: `${PREFIX}.duplicatedNationalIdTitle`,
      subtitleKey: `${PREFIX}.duplicatedNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.DuplicatedPassport]: {
      // NEW: Updated from generic error with retry
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.duplicatedPassportSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.ExcessiveActivity]: {
      // NEW: Simplified to just search again
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.ExpiredNationalId]: {
      // NEW: Updated from generic error with retry
      titleKey: `${PREFIX}.passengerValidationTitle`,
      subtitleKey: `${PREFIX}.expiredNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.ExpiredPassport]: {
      titleKey: `${PREFIX}.expiredPassportTitle`,
      subtitleKey: `${PREFIX}.expiredPassportSubtitle`,
      // NEW: Updated action to open passport form
      primaryButton: BUTTONS.openPassportForm,
      className: "missing",
    },

    [FlightError.ExpiringNationalId]: {
      // NEW: Updated from generic error with retry
      titleKey: `${PREFIX}.passengerValidationTitle`,
      subtitleKey: `${PREFIX}.expiringNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.ExpiringPassport]: {
      titleKey: `${PREFIX}.expiringPassportTitle`,
      subtitleKey: `${PREFIX}.expiringPassportSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.ExtraBagsError]: {
      // NEW: Simplified to search again
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.IllegalLapInfantKTN]: {
      titleKey: `${PREFIX}.illegalLapInfantKTNTitle`,
      subtitleKey: `${PREFIX}.illegalLapInfantKTNSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.IllegalLapInfantRedress]: {
      titleKey: `${PREFIX}.illegalLapInfantRedressTitle`,
      subtitleKey: `${PREFIX}.illegalLapInfantRedressSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.Inactivity]: {
      titleKey: `${PREFIX}.flightsInActivityTitle`,
      subtitleKey: `${PREFIX}.flightsInActivitySubtitle`,
      primaryButton: BUTTONS.findNewFlight,
      className: "timeout",
    },

    [FlightError.InvalidCustomer]: {
      titleKey: `${PREFIX}.invalidCustomerTitle`,
      subtitleKey: `${PREFIX}.invalidCustomerSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.InvalidPassengerName]: {
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.invalidPassengerName`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.InvalidPassengers]: {
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.invalidPassengerSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.InvalidPassportNumber]: {
      titleKey: `${PREFIX}.invalidPassportNumberTitle`,
      subtitleKey: `${PREFIX}.invalidPassportNumberSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.InvalidPassportOrNationalIdNumber]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.invalidPassengerSubtitle`,
      primaryButton: BUTTONS.updateTravelers,
      className: "missing",
    },

    [FlightError.LapInfantsUnsupported]: {
      titleKey: `${PREFIX}.lapInfantsUnsupportedTitle`,
      subtitleKey: `${PREFIX}.lapInfantsUnsupportedSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.LapInfantTooOld]: {
      titleKey: `${PREFIX}.lapInfantTooOldTitle`,
      subtitleKey: `${PREFIX}.lapInfantTooOldSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.MalformedKnownTravelerNumber]: {
      titleKey: `${PREFIX}.malformedKnownTravelerNumberTitle`,
      subtitleKey: `${PREFIX}.malformedKnownTravelerNumberSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.MalformedRedressNumber]: {
      titleKey: `${PREFIX}.malformedRedressNumberTitle`,
      subtitleKey: `${PREFIX}.malformedRedressNumberSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.MissingAirlineLocator]: {
      titleKey: `${PREFIX}.missingAirlineLocatorTitle`,
      subtitleKey: `${PREFIX}.missingAirlineLocatorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.MissingNationalId]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.passengerValidationTitle`,
      subtitleKey: `${PREFIX}.missingNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.MissingNationality]: {
      titleKey: `${PREFIX}.missingNationalityTitle`,
      subtitleKey: `${PREFIX}.missingNationalitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
      className: "missing",
    },

    [FlightError.MissingPassport]: {
      titleKey: `${PREFIX}.missingPassportTitle`,
      subtitleKey: `${PREFIX}.missingPassportSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.MissingPassportNumber]: {
      titleKey: `${PREFIX}.missingPassportNumberTitle`,
      subtitleKey: `${PREFIX}.missingPassportNumberSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.MissingPassportOrNationalId]: {
      titleKey: `${PREFIX}.missingPassportOrNationalIdTitle`,
      subtitleKey: `${PREFIX}.missingPassportOrNationalIdSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.NoAdultPassenger]: {
      titleKey: `${PREFIX}.noAdultPassengerTitle`,
      subtitleKey: `${PREFIX}.noAdultPassengerSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.NoAvailability]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.NoContactInformation]: {
      titleKey: `${PREFIX}.noContactInformationTitle`,
      subtitleKey: `${PREFIX}.noContactInformationSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.NoTicketlessResponse]: {
      titleKey: `${PREFIX}.noTicketlessResponseTitle`,
      subtitleKey: `${PREFIX}.noTicketlessResponseSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.PassengerNameTooShort]: {
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.passengerNameTooShortSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.PassengerNotFound]: DEFAULT_CART_FULFILL_CONFIG,

    [FlightError.PassthroughPaymentError]: DEFAULT_CART_FULFILL_CONFIG,

    [FlightError.ProviderError]: {
      titleKey: `${PREFIX}.providerErrorTitle`,
      subtitleKey: `${PREFIX}.providerErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.SeatedInfantsUnsupported]: {
      titleKey: `${PREFIX}.seatedInfantsUnsupportedTitle`,
      subtitleKey: `${PREFIX}.seatedInfantsUnsupportedSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.SessionClosed]: DEFAULT_CART_FULFILL_CONFIG,

    [FlightError.ShopAsyncError]: DEFAULT_CART_FULFILL_CONFIG,

    [FlightError.TicketingNotStarted]: {
      titleKey: `${PREFIX}.ticketingNotStartedTitle`,
      subtitleKey: `${PREFIX}.ticketingNotStartedSubtitle`,
      primaryButton: BUTTONS.empty,
    },

    [FlightError.TicketingUnconfirmed]: {
      titleKey: `${PREFIX}.ticketingUnconfirmedTitle`,
      subtitleKey: `${PREFIX}.ticketingUnconfirmedSubtitle`,
      primaryButton: BUTTONS.empty,
    },

    [FlightError.TicketingUnknown]: {
      titleKey: `${PREFIX}.ticketingUnknownTitle`,
      subtitleKey: `${PREFIX}.ticketingUnknownSubtitle`,
      primaryButton: BUTTONS.empty,
    },

    [FlightError.TooManyLapInfants]: {
      titleKey: `${PREFIX}.tooManyLapInfantsTitle`,
      subtitleKey: `${PREFIX}.tooManyLapInfantsSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.TooManyPassengers]: {
      titleKey: `${PREFIX}.tooManyPassengersTitle`,
      subtitleKey: `${PREFIX}.tooManyPassengersSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.TransientServiceError]: {
      titleKey: `${PREFIX}.transientServiceErrorTitle`,
      subtitleKey: `${PREFIX}.transientServiceErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.UnexpectedPassengers]: {
      // NEW: Updated from generic error
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.unexpectedPassengersSubtitle`,
      primaryButton: BUTTONS.findNewFlight,
    },

    [FlightError.Unknown]: DEFAULT_CART_FULFILL_CONFIG,

    [FlightError.UnknownSabreAppError]: {
      titleKey: `${PREFIX}.unknownSabreAppErrorTitle`,
      subtitleKey: `${PREFIX}.unknownSabreAppErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },

    [FlightError.UnsupportedNameCharacters]: {
      titleKey: `${PREFIX}.invalidPassenger`,
      subtitleKey: `${PREFIX}.unsupportedNameCharactersSubtitle`,
      primaryButton: BUTTONS.updateTravelersGoBack,
      className: "missing",
    },

    [FlightError.UnsupportedPaymentKind]: DEFAULT_CART_FULFILL_CONFIG,

    [FlightError.UpgradeRequired]: {
      titleKey: `${PREFIX}.upgradeRequiredTitle`,
      subtitleKey: `${PREFIX}.unknownSabreAppErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
  },
};

/**
 * Purchase-level error configuration for errors that aren't FlightError specific
 * These are errors like AuthError, ExpiredFulfillToken, etc.
 */
export const PURCHASE_ERROR_CONFIG: PurchaseErrorConfigMap = {
  CART_QUOTE: {
    [PurchaseErrorEnum.PaymentError]: {
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.AuthError]: {
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.ExpiredFulfillToken]: {
      // NEW: Updated to show no availability message
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.ExpiredQuoteToken]: {
      // NEW: Updated subtitle
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorRetrySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.FraudAutoReject]: {
      // NEW: Updated messaging
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.InActivity]: {
      // NEW: Updated messaging
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.InvalidSession]: {
      // NEW: Updated messaging
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.ErrorCode]: {
      // NEW: Updated subtitle
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorRetrySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.NoAvailability]: {
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
  },
  CART_FULFILL: {
    [PurchaseErrorEnum.PaymentError]: {
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.AuthError]: {
      // NEW: Simplified to just search again
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.ExpiredFulfillToken]: {
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.ExpiredQuoteToken]: {
      // NEW: Updated subtitle
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorRetrySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.FraudAutoReject]: {
      // NEW: Updated messaging
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.InActivity]: {
      // NEW: Simplified
      titleKey: `${PREFIX}.flightsInActivityTitle`,
      subtitleKey: `${PREFIX}.flightsInActivitySubtitle`,
      primaryButton: BUTTONS.findNewFlight,
    },
    [PurchaseErrorEnum.InvalidSession]: {
      // NEW: Updated messaging
      titleKey: `${PREFIX}.oopsSomethingWrongTitle`,
      subtitleKey: `${PREFIX}.pleaseSearchAgainSubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.ErrorCode]: {
      // NEW: Simplified
      titleKey: `${PREFIX}.genericErrorTitle`,
      subtitleKey: `${PREFIX}.genericErrorRetrySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
    [PurchaseErrorEnum.NoAvailability]: {
      titleKey: `${PREFIX}.noAvailabilityTitle`,
      subtitleKey: `${PREFIX}.noAvailabilitySubtitle`,
      primaryButton: BUTTONS.searchAgain,
    },
  },
};

/**
 * Seat Selection Error Configuration
 *
 * Maps SeatSelectionErrorModal codes to error modal configuration.
 * These errors occur during seat selection in the flight checkout flow.
 */
export const SEAT_SELECTION_ERROR_CONFIG: SeatSelectionErrorConfigMap = {
  default: {
    titleKey: `${PREFIX}.genericErrorTitle`,
    subtitleKey: `${PREFIX}.genericErrorSubtitle`,
    primaryButton: BUTTONS.searchAgain,
  },

  [SeatSelectionErrorModal.BrokenCart]: {
    titleKey: `${PREFIX}.genericErrorTitle`,
    subtitleKey: `${PREFIX}.genericErrorSubtitle`,
    primaryButton: BUTTONS.searchAgain,
  },

  [SeatSelectionErrorModal.FailedAddToCart]: {
    titleKey: "seats.errorFailedAddToCartTitle",
    subtitleKey: "seats.errorFailedAddToCartSubtitle",
    primaryButton: {
      textKey: "seats.continueWithoutSeats",
      action: FlightErrorAction.ContinueWithoutSeats,
    },
    secondaryButton: BUTTONS.searchAgain,
  },

  [SeatSelectionErrorModal.GenericError]: {
    titleKey: `${PREFIX}.genericErrorTitle`,
    subtitleKey: `${PREFIX}.genericErrorSubtitle`,
    primaryButton: BUTTONS.searchAgain,
  },
};

/**
 * Helper to get flight error config with fallback to default
 */
export const getFlightErrorConfig = (
  context: "CART_QUOTE" | "CART_FULFILL",
  errorCode: FlightError
): FlightErrorConfig => {
  return (
    FLIGHT_ERROR_CONFIG[context][errorCode] ??
    FLIGHT_ERROR_CONFIG[context].default
  );
};

/**
 * Helper to get purchase error config with fallback
 */
export const getPurchaseErrorConfig = (
  context: "CART_QUOTE" | "CART_FULFILL",
  category: PurchaseErrorEnum
): FlightErrorConfig | undefined => {
  return PURCHASE_ERROR_CONFIG[context][
    category as keyof (typeof PURCHASE_ERROR_CONFIG)[typeof context]
  ];
};

/**
 * Helper to get seat selection error config with fallback to default
 */
export const getSeatSelectionErrorConfig = (
  errorCode: SeatSelectionErrorModal
): FlightErrorConfig => {
  return (
    SEAT_SELECTION_ERROR_CONFIG[errorCode] ??
    SEAT_SELECTION_ERROR_CONFIG.default
  );
};
